import { onLanguageChanged } from '@thiememeulenhoff/translations';
import { LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import {
  UserActivityEvent,
  UserActivityEventType,
} from '@thiememeulenhoff/types';
import { dispatchCustomEvent } from './event.helper';

type DispatchCustomEvent = {
  eventName: string;
  element?: LitElement | Document | HTMLElement;
  detail?: CustomEvent['detail'];
  bubbles?: CustomEvent['bubbles'];
  cancelable?: CustomEvent['cancelable'];
};

type AllowedElements = Element | HTMLElement | Document | Window;

export interface EventListener {
  name: string;
  callback: (event: Event) => void;
  element: AllowedElements;
}

export class TmElement extends LitElement {
  /**
   * Property to set the `data-test` id attribute to the host element.
   * It is auto filled with the element's tag name.
   */
  @property({
    attribute: 'data-test',
    reflect: true,
  })
  protected testId: string = this.tagName.toLowerCase();

  protected onCreate?: () => void;

  protected onDestroy?: () => void;

  #listeners: Array<EventListener> = [];

  /**
   * Add event listeners that will automatically be removed on component destroy.
   * Use last parameter to specify the context (defaults to `this`).
   *
   * @param nameOrNames build-in `Event` name, or array of names
   * @param callback function that is executed, with `Event` as param
   * @param element any `HTMLElement` to add the listener to.
   *
   * @example
   * // Listen for click events...
   * this.listen('click', this.#handleClick);
   * // ...which is the same as:
   * this.listen('click', this.#handleClick, this);
   * // Listen for multiple events
   * this.listen(['click', 'keydown'], this.#handleEvent);
   */
  protected listen(
    nameOrNames: string | Array<string>,
    callback: (event: Event) => void,
    element: AllowedElements = this,
  ) {
    const names = Array.isArray(nameOrNames) ? nameOrNames : [nameOrNames];
    names.forEach((name) => this.#addEventListener(name, callback, element));
  }

  override connectedCallback() {
    super.connectedCallback();
    this.onCreate?.();

    this.listen('focusin', (event) => {
      event.stopPropagation();

      document.dispatchEvent(
        new CustomEvent('customOnFocus', { detail: event.composedPath() }),
      );
    });
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEventListeners();
    this.onDestroy?.();
  }

  constructor() {
    super();

    onLanguageChanged(() => this.requestUpdate());
  }

  /**
   * This "Local" event dispatcher will stay in
   * the ShadowDOM because "composed" is false.
   */
  public dispatchLocalEvent({
    element = this,
    eventName,
    detail,
    bubbles = true,
    cancelable = false,
  }: DispatchCustomEvent) {
    return dispatchCustomEvent({
      element,
      eventName,
      detail,
      bubbles,
      cancelable,
      composed: false,
    });
  }

  /**
   * This "Global" event dispatcher will propagate
   * to the normal DOM because "composed" is true.
   */
  public dispatchGlobalEvent({
    element = this,
    eventName,
    detail,
    bubbles = true,
    cancelable = true,
  }: DispatchCustomEvent) {
    return dispatchCustomEvent({
      element,
      eventName,
      detail,
      bubbles,
      cancelable,
      composed: true,
    });
  }

  dispatchActivityEvent(type: UserActivityEventType, data: UserActivityEvent) {
    this.dispatchGlobalEvent({
      eventName: 'tmwc-activity',
      detail: {
        type,
        ...data,
      },
    });
  }

  public get slottedChildren() {
    const slot = this.shadowRoot?.querySelector('slot');
    return slot?.assignedElements({ flatten: true });
  }

  #addEventListener(
    name: string,
    callback: (event: Event) => void,
    element: AllowedElements = this,
  ) {
    element.addEventListener(name, callback);
    this.#listeners.push({
      name,
      callback,
      element,
    });
  }

  #removeEventListeners() {
    this.#listeners.forEach(({ name, element, callback }) =>
      element.removeEventListener(name, callback),
    );
  }
}
